import { createContext, useCallback, useContext, useMemo } from "react";

import { Box } from "@mantine/core";
import { TimeInput } from "@mantine/dates";

import { IntakeFormComponentProps } from "@/intake-forms/types/intake-forms";
import { setFormFieldValue } from "@/intake-forms/utils/intake-forms";
import { questionnaireFieldTestId } from "@/intake-forms/utils/questionnaire-field-testid";
import { localTimeFromISO } from "@/utils/datetime";
import { useControlledTimeInput } from "@/utils/hooks/useControlledTimeInput";
import {
  getCurrentLocalTimeString,
  localToUtcTimeString,
  utcToLocalTimeString,
} from "@/utils/timeUtils";

/** Set by labor drawer during edit so UTC `valueTime` displays using the same instant as the table. */
export const QuestionnaireUtcDateAnchorContext = createContext<Record<string, string> | null>(null);

export default function QuestionnaireTimeField<T>({
  form,
  formFieldName,
  label,
  required,
  name,
  placeholder,
  leftSection,
  clearable,
}: Omit<IntakeFormComponentProps<T>, "key"> & {
  error?: string;
  leftSection?: React.ReactNode;
  clearable?: boolean;
}) {
  const formInputProps = form.getInputProps(formFieldName);
  const utcDateAnchors = useContext(QuestionnaireUtcDateAnchorContext);
  const utcDateAnchor = useMemo(() => {
    const parts = formFieldName.split(".");
    if (parts.length < 2) return undefined;
    const parentLinkId = parts[parts.length - 2];
    if (!parentLinkId.includes("DateTime")) return undefined;
    return utcDateAnchors?.[parentLinkId];
  }, [formFieldName, utcDateAnchors]);

  const storedToDisplay = useCallback(
    (value: string) => {
      if (/^\d{2}:\d{2}:\d{2}$/.test(value)) {
        if (utcDateAnchor && /^\d{4}-\d{2}-\d{2}$/.test(utcDateAnchor)) {
          return localTimeFromISO(`${utcDateAnchor}T${value}Z`);
        }
        return utcToLocalTimeString(value);
      }
      return value;
    },
    [utcDateAnchor],
  );

  const onCommit = useCallback(
    (value: string) => {
      setFormFieldValue(form, formFieldName, value);
    },
    [form, formFieldName],
  );

  const getAutoPopulateValue = useCallback(() => {
    const localTimeString = getCurrentLocalTimeString();
    return {
      display: localTimeString,
      stored: localToUtcTimeString(localTimeString),
    };
  }, []);

  const { displayValue, inputRef, onFocus, onBlur, onChange, openPicker } = useControlledTimeInput({
    storedValue: formInputProps.value as string | null | undefined,
    onCommit,
    storedToDisplay,
    displayToStored: localToUtcTimeString,
    getAutoPopulateValue,
  });

  const timeInputLeftSection = leftSection ? (
    <Box
      component="span"
      style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
      onClick={(event) => {
        event.stopPropagation();
        openPicker();
      }}
    >
      {leftSection}
    </Box>
  ) : undefined;

  return (
    <TimeInput
      key={name}
      withSeconds
      name={name}
      label={label}
      ref={inputRef}
      onBlur={onBlur}
      onFocus={onFocus}
      onChange={onChange}
      value={displayValue}
      withAsterisk={required}
      placeholder={placeholder}
      leftSection={timeInputLeftSection}
      error={form.errors[formFieldName]}
      data-testid={questionnaireFieldTestId(formFieldName)}
    />
  );
}
